import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { ChartPoint, PLATFORM_LOGO, formatMetric, metricLabel, niceMax, relativeDay, shortDay } from '../../hybrid-reporting.helpers';
import { ReportMetric } from '../../hybrid-reporting.types';

const W = 760;
const H = 230;
const PAD = { top: 14, right: 12, bottom: 30, left: 48 };

type Tick = { y: number; label: string };
type XLabel = { x: number; label: string };
type Dot = { x: number; r: number };

/**
 * Running total of one metric since the brief started: a pink line and area, dots on the
 * baseline for days a post went live, a hover guide with a tooltip, and a dashed pin for the
 * post open in the drawer.
 */
@Component({
  selector: 'hr-chart',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="rp-cbox" [class.hov]="hover() !== null" (pointerleave)="hover.set(null)">
      <svg [attr.viewBox]="'0 0 ' + W + ' ' + H" role="img" [attr.aria-label]="ariaLabel()" (pointermove)="onMove($event)">
        <defs>
          <linearGradient id="hr-grad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stop-color="#DB24BF" stop-opacity="0.22" />
            <stop offset="1" stop-color="#DB24BF" stop-opacity="0" />
          </linearGradient>
        </defs>
        <g class="ax">
          @for (t of ticks(); track t.y; let first = $first) {
            <line [class]="first ? 'gl base' : 'gl'" [attr.x1]="PAD.left" [attr.x2]="W - PAD.right" [attr.y1]="t.y" [attr.y2]="t.y" />
            <text [attr.x]="PAD.left - 8" [attr.y]="t.y + 4" text-anchor="end">{{ t.label }}</text>
          }
          @for (l of xLabels(); track l.x) {
            <text [attr.x]="l.x" [attr.y]="H - 8" text-anchor="middle">{{ l.label }}</text>
          }
        </g>
        @if (areaPath()) {
          <path class="ar" [attr.d]="areaPath()" fill="url(#hr-grad)" />
          <path class="ln" [attr.d]="linePath()" />
        }
        @for (d of dots(); track d.x) {
          <circle class="pd" [attr.cx]="d.x" [attr.cy]="baseY" [attr.r]="d.r" />
        }
        @if (pin(); as p) {
          <line class="pin" [attr.x1]="p.x" [attr.x2]="p.x" [attr.y1]="PAD.top" [attr.y2]="baseY" />
          <rect class="pinb" [attr.x]="p.boxX" [attr.y]="PAD.top - 2" [attr.width]="p.w" height="20" rx="6" />
          <text class="pint" [attr.x]="p.boxX + p.w / 2" [attr.y]="PAD.top + 12" text-anchor="middle">{{ p.label }}</text>
        }
        @if (hoverPoint(); as h) {
          <line class="guide" [attr.x1]="h.x" [attr.x2]="h.x" [attr.y1]="PAD.top" [attr.y2]="baseY" />
          <circle class="hdot" [attr.cx]="h.x" [attr.cy]="h.y" r="5" />
        }
        <rect class="hit" [attr.x]="PAD.left" [attr.y]="0" [attr.width]="W - PAD.left - PAD.right" [attr.height]="H" />
      </svg>
      @if (hoverPoint(); as h) {
        <div class="mm-tip" role="status" [style.left.%]="h.leftPct" [style.top.%]="h.topPct">
          <span class="mo-k">{{ h.day }}</span>
          <b>{{ h.value }}</b>
          <span class="mo-sub">{{ h.caption }}</span>
          @for (p of h.posted; track p.postId) {
            <span class="tp">
              @if (p.platform) {<img [src]="logos[p.platform]" alt="" class="size-3 object-contain" />}
              {{ p.creator }} posted {{ p.label }}
            </span>
          }
        </div>
      }
    </div>
  `,
})
export class HrChart {
  readonly points = input.required<ChartPoint[]>();
  readonly metric = input.required<ReportMetric>();
  readonly currencyCode = input('gbp');
  /** Day and creator of the post open in the drawer. */
  readonly pinned = input<{ date: string; creator: string } | null>(null);
  readonly now = input.required<Date>();

  protected readonly W = W;
  protected readonly H = H;
  protected readonly PAD = PAD;
  protected readonly logos = PLATFORM_LOGO;
  protected readonly baseY = H - PAD.bottom;
  protected readonly hover = signal<number | null>(null);

  /** Counts get a floor of 4 so small numbers don't produce repeated rounded tick labels. */
  private readonly max = computed(() => {
    const top = niceMax(Math.max(0, ...this.points().map((p) => p.value)) * 1.08);
    const metric = this.metric();
    return metric === 'er' || metric === 'cpm' ? top : Math.max(4, top);
  });

  private x(i: number): number {
    const n = this.points().length;
    return PAD.left + (n <= 1 ? 0 : (i / (n - 1)) * (W - PAD.left - PAD.right));
  }

  private y(v: number): number {
    return this.baseY - (v / this.max()) * (this.baseY - PAD.top);
  }

  protected readonly ticks = computed<Tick[]>(() => {
    const max = this.max();
    return [0, 1, 2, 3, 4].map((i) => {
      const v = (max / 4) * i;
      return { y: this.y(v), label: formatMetric(this.metric(), v, this.currencyCode()) };
    });
  });

  protected readonly xLabels = computed<XLabel[]>(() => {
    const pts = this.points();
    // At most ~8 labels however long the brief has run, so dates never overlap.
    const every = Math.max(1, Math.ceil(pts.length / 8));
    const last = pts.length - 1;
    const withYear = !!pts.length && pts[0].date.slice(0, 4) !== pts[last].date.slice(0, 4);
    return pts
      .map((p, i) => ({ x: this.x(i), label: i === last ? 'Today' : shortDay(p.date, withYear), i }))
      .filter((l) => l.i === last || (l.i % every === 0 && last - l.i >= every))
      .map(({ x, label }) => ({ x, label }));
  });

  protected readonly linePath = computed(() =>
    this.points()
      .map((p, i) => `${i ? 'L' : 'M'}${this.x(i).toFixed(1)},${this.y(p.value).toFixed(1)}`)
      .join(''),
  );

  protected readonly areaPath = computed(() => {
    const pts = this.points();
    if (pts.length < 2) return '';
    return `${this.linePath()}L${this.x(pts.length - 1).toFixed(1)},${this.baseY}L${this.x(0).toFixed(1)},${this.baseY}Z`;
  });

  protected readonly dots = computed<Dot[]>(() =>
    this.points()
      .map((p, i) => ({ x: this.x(i), r: p.posted.length > 1 ? 6 : 4.5, n: p.posted.length }))
      .filter((d) => d.n > 0)
      .map(({ x, r }) => ({ x, r })),
  );

  protected readonly pin = computed(() => {
    const pinned = this.pinned();
    if (!pinned) return null;
    const i = this.points().findIndex((p) => p.date === pinned.date.slice(0, 10));
    if (i < 0) return null;
    const label = `${pinned.creator} posted`;
    const w = Math.max(60, label.length * 6.4 + 16);
    const x = this.x(i);
    return { x, w, label, boxX: Math.min(Math.max(x - w / 2, PAD.left), W - PAD.right - w) };
  });

  protected readonly hoverPoint = computed(() => {
    const i = this.hover();
    const pts = this.points();
    if (i === null || !pts[i]) return null;
    const p = pts[i];
    const metric = this.metric();
    const label = metricLabel(metric);
    const x = this.x(i);
    const y = this.y(p.value);
    return {
      x,
      y,
      leftPct: (x / W) * 100,
      topPct: (y / H) * 100,
      day: relativeDay(p.date, this.now()),
      value: formatMetric(metric, p.value, this.currencyCode()),
      caption: metric === 'er' || metric === 'cpm' ? label : `${label} so far`,
      posted: p.posted,
    };
  });

  protected readonly ariaLabel = computed(() => {
    const pts = this.points();
    const last = pts[pts.length - 1];
    return `${metricLabel(this.metric())} over time${last ? `, ${formatMetric(this.metric(), last.value, this.currencyCode())} today` : ''}`;
  });

  protected onMove(event: PointerEvent): void {
    const svg = event.currentTarget as SVGSVGElement;
    const rect = svg.getBoundingClientRect();
    const n = this.points().length;
    if (!n || !rect.width) return;
    const vx = ((event.clientX - rect.left) / rect.width) * W;
    const ratio = (vx - PAD.left) / (W - PAD.left - PAD.right);
    this.hover.set(Math.min(n - 1, Math.max(0, Math.round(ratio * (n - 1)))));
  }
}
